/**
 * Flower / plant-type checkbox list.
 *
 * Deliberately a near-exact mirror of `ColourSection` (same `FilterSection`
 * shell, same checkbox row, same "count = selected.length" badge) so the two
 * facets read identically in the sidebar and the sheet.
 *
 * The `flowers` filter had been fully built server-side (`listProducts`'s
 * `flowers` param, the AI content detection, `scripts/backfill-flowers.mjs`,
 * an admin review UI) yet had NO customer or crawler surface at all — its only
 * consumer was the chatbot, and `/api/products` didn't even read the param. So
 * "sunflowers" was answerable in chat and unreachable everywhere else.
 *
 * The list itself is sourced from REAL data (`listFlowerFacets` reads distinct
 * names out of `products.flowers`), never a hardcoded invented vocabulary —
 * same principle as the colour list coming from admin settings.
 */

"use client";

import { Checkbox } from "@/components/ui/checkbox";
import { FilterSection } from "./FilterSection";

interface FlowerSectionProps {
  flowers: string[];
  selected: string[];
  onToggle: (f: string) => void;
  isOpen: boolean;
  onToggleSection: (title: string) => void;
}

export function FlowerSection({
  flowers,
  selected,
  onToggle,
  isOpen,
  onToggleSection,
}: FlowerSectionProps) {
  // No detected flowers yet (fresh install / backfill not run) → render
  // nothing rather than an empty, dead-end disclosure.
  if (flowers.length === 0) return null;
  return (
    <FilterSection
      title="Flower"
      count={selected.length}
      isOpen={isOpen}
      onToggle={onToggleSection}
    >
      <div className="flex flex-col gap-2">
        {flowers.map((f) => (
          <label
            key={f}
            className="flex cursor-pointer items-center gap-3 py-1 text-sm text-foreground"
          >
            <Checkbox
              checked={selected.includes(f)}
              onCheckedChange={() => onToggle(f)}
            />
            <span>{f}</span>
          </label>
        ))}
      </div>
    </FilterSection>
  );
}
